"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { getImageUrl } from "@/utils/utils";

interface HomePageIntroClientProps {
  lang: string;
  homepageContent: any;
}

export default function HomePageIntroClient({
  lang,
  homepageContent,
}: HomePageIntroClientProps) {
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection for optimized animations
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Animation variants for bottom-to-top reveal - exact match from original
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: isMobile ? 30 : 60, // Reduced movement on mobile
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: isMobile ? 0.6 : 0.8, // Faster animation on mobile
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <motion.section
      className="flex flex-col lg:flex-row items-stretch w-full"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={sectionVariants}
    >
      {/* Logo Container - 30% Width */}
      <div className="about-logo-bg flex flex-col items-start justify-center w-full lg:w-[30%] min-h-[68px] sm:min-h-[200px] lg:min-h-[240px] px-4 py-4 sm:px-8 sm:py-8 lg:px-[62px] lg:py-[72px]">
        {homepageContent.intro?.image && (
          <Image
            src={getImageUrl(homepageContent.intro?.image.url || "")}
            alt="Nippon Koei Bangladesh"
            width={304}
            height={96}
            className="object-contain max-w-40 lg:max-w-full h-auto w-full"
          />
        )}
      </div>

      {/* Years in Business Section - 18% Width */}
      <div className="about-years-bg flex flex-col items-start justify-center w-full lg:w-[18%] min-h-[100px] sm:min-h-[200px] lg:min-h-[240px] px-4 py-4 sm:px-8 sm:py-8 lg:px-[57px] lg:py-[78px]">
        <div className="text-white text-[32px] sm:text-[40px] lg:text-[50px] font-medium leading-[38px] sm:leading-[48px] lg:leading-[58px] tracking-[-1.44px]">
          {homepageContent.intro?.yearsInBusinessNumbers}+
        </div>
        <div className="text-white text-[14px] sm:text-[15px] lg:text-[17px] font-normal leading-[16px] lg:leading-[17.6px] capitalize">
          {homepageContent.intro?.yearsInBusinessLabel}
        </div>
      </div>

      {/* Description Section - 52% Width, Figma spec */}
      <div className="w-full lg:w-[52%] bg-[#194843] px-4 py-4 sm:px-8 sm:py-8 lg:px-[54px] lg:py-[44px] flex items-center justify-center">
        <p className="text-white font-inter text-[16px] sm:text-[18px] lg:text-[20px] font-normal leading-[24px] sm:leading-[27px] lg:leading-[30px]">
          {homepageContent.intro?.description}
        </p>
      </div>
    </motion.section>
  );
}
